import { Box, SxProps, useTheme } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useMemo } from 'react';
import {
  BlueberryColorsMap,
  FeatureEnum,
} from '../../../../services/data/impl/cube/cubeSlice';
import { UnitsEnum } from '../../../../services/entities/impl/blfApi/apiConstants';
import { useTranslation } from '../../../../services/translation/hooks/useTranslation';
import { flexCenter } from '../../../commonUI/cssClasses';
import { roundFractional } from '../../../ranches/ranchUtils';
import { ScanDataEnum } from '../../../scanDataApi/scanDataDefinitions';
import { useUnits } from '../../../settings/hooks/useUnits';
import { EstimatedCountSumQueryResult } from './MapMeasureDialogContent';

function createData(
  rowName: string,
  green: string | null,
  pink: string | null,
  purple: string | null,
  blue: string | null,
  berriesTotal: string | null,
  flowersClosed: string | null,
  flowersOpen: string | null,
  flowersPetalfall: string | null,
  flowersTotal: string | null,
  totalDistance: string | null,
  avgVelocity: string | null
) {
  return {
    rowName,
    green,
    pink,
    purple,
    blue,
    berriesTotal,
    flowersClosed,
    flowersOpen,
    flowersPetalfall,
    flowersTotal,
    totalDistance,
    avgVelocity,
  };
}

const createTableRow = (
  data: EstimatedCountSumQueryResult[],
  rowName: string,
  avgVelocity: number
) => {
  let green = 0;
  let pink = 0;
  let purple = 0;
  let blue = 0;
  let flowersClosed = 0;
  let flowersOpen = 0;
  let flowersPetalfall = 0;
  let maxTotalDistance = 0;

  for (const item of data) {
    const className = item[ScanDataEnum.class_name];

    // Check and aggregate for blueberries and flowers
    if (className === FeatureEnum.blueberry_Green) {
      green = item.totalCount;
    } else if (className === FeatureEnum.blueberry_Yellow) {
      pink = item.totalCount;
    } else if (className === FeatureEnum.blueberry_Purple) {
      purple = item.totalCount;
    } else if (className === FeatureEnum.blueberry_Blue) {
      blue = item.totalCount;
    } else if (className === FeatureEnum.flower_closed) {
      flowersClosed = item.totalCount;
    } else if (className === FeatureEnum.flower_open) {
      flowersOpen = item.totalCount;
    } else if (className === FeatureEnum.flower_petalfall) {
      flowersPetalfall = item.totalCount;
    }

    // Track maximum totalDistanceTraveled
    if (item.totalDistanceTraveled > maxTotalDistance) {
      maxTotalDistance = item.totalDistanceTraveled;
    }
  }

  // Calculate totals after the loop
  const berriesTotal = green + pink + purple + blue;
  const flowersTotal = flowersClosed + flowersOpen + flowersPetalfall;

  const roundDigits = 0;

  const greenFormatted = roundFractional(green, roundDigits);
  const pinkFormatted = roundFractional(pink, roundDigits);
  const purpleFormatted = roundFractional(purple, roundDigits);
  const blueFormatted = roundFractional(blue, roundDigits);
  const berriesTotalFormatted = roundFractional(berriesTotal, roundDigits);
  const flowersClosedFormatted = roundFractional(flowersClosed, roundDigits);
  const flowersOpenFormatted = roundFractional(flowersOpen, roundDigits);
  const flowersPetalfallFormatted = roundFractional(
    flowersPetalfall,
    roundDigits
  );
  const flowersTotalFormatted = roundFractional(flowersTotal, roundDigits);
  const totalDistanceFormatted = roundFractional(maxTotalDistance, roundDigits);
  const velocityFormatted = roundFractional(avgVelocity, roundDigits);

  return createData(
    rowName,
    greenFormatted,
    pinkFormatted,
    purpleFormatted,
    blueFormatted,
    berriesTotalFormatted,
    flowersClosedFormatted,
    flowersOpenFormatted,
    flowersPetalfallFormatted,
    flowersTotalFormatted,
    totalDistanceFormatted,
    velocityFormatted
  );
};

interface Props {
  blueberryData: EstimatedCountSumQueryResult[];
  avgVelocity: number;
}

const MapMeasureTableBlueberries: React.FC<Props> = ({
  blueberryData,
  avgVelocity,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const units = useUnits();

  const commonCellStyles: SxProps = {
    p: 1,
    bgcolor: theme.colors.darkTheme.grey.dark,
    color: theme.colors.common.white,
    borderBottom: `1px solid ${theme.colors.darkTheme.grey.muted}`,
  };

  const cellStyles: SxProps = {
    ...commonCellStyles,
    flexGrow: 1, // Set flexGrow to 1
  };

  const firstColumnStyles: SxProps = {
    ...commonCellStyles,
    width: '1%', // Make the first column's width as small as possible
    whiteSpace: 'nowrap', // Prevent text wrapping
    pl: 1.5,
  };

  const rows = useMemo(() => {
    const row1Title = `${t('Estimated Count', { ns: 'common' })}`;

    const defaultEmptyRows = [
      createData(
        row1Title,
        '-',
        '-',
        '-',
        '-',
        '-',
        '-',
        '-',
        '-',
        '-',
        '-',
        '-'
      ),
    ];

    if (!blueberryData) {
      return defaultEmptyRows;
    } else {
      const estimatedCountRow = createTableRow(
        blueberryData,
        row1Title,
        avgVelocity
      );

      return [estimatedCountRow];
    }
  }, [blueberryData]);

  return (
    <TableContainer
      component={Paper}
      sx={{ width: '100%', ...flexCenter, borderRadius: 0 }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          width: '100%',
        }}
      >
        <Table
          sx={{ minWidth: '100%' }}
          size='small'
          aria-label='a dense table'
        >
          <TableHead>
            <TableRow>
              <TableCell align='center' sx={{ ...firstColumnStyles }}>
                <Box sx={{ width: '100%', ...flexCenter }}>{status}</Box>
              </TableCell>
              <TableCell
                align='center'
                sx={{
                  ...cellStyles,
                  color: BlueberryColorsMap[FeatureEnum.flower_closed],
                }}
              >
                {t(FeatureEnum.flower_closed, { ns: 'common' })}
              </TableCell>
              <TableCell
                align='center'
                sx={{
                  ...cellStyles,
                  color: BlueberryColorsMap[FeatureEnum.flower_open],
                }}
              >
                {t(FeatureEnum.flower_open, { ns: 'common' })}
              </TableCell>
              <TableCell
                align='center'
                sx={{
                  ...cellStyles,
                  color: BlueberryColorsMap[FeatureEnum.flower_petalfall],
                }}
              >
                {t(FeatureEnum.flower_petalfall, { ns: 'common' })}
              </TableCell>
              <TableCell align='center' sx={{ ...cellStyles }}>
                {t('Total Flowers', { ns: 'common' })}
              </TableCell>
              <TableCell
                align='center'
                sx={{
                  ...cellStyles,
                  color: BlueberryColorsMap[FeatureEnum.blueberry_Green],
                }}
              >
                {t(FeatureEnum.blueberry_Green, { ns: 'common' })}
              </TableCell>
              <TableCell
                align='center'
                sx={{
                  ...cellStyles,
                  color: BlueberryColorsMap[FeatureEnum.blueberry_Yellow],
                }}
              >
                {t(FeatureEnum.blueberry_Yellow, { ns: 'common' })}
              </TableCell>
              <TableCell
                align='center'
                sx={{
                  ...cellStyles,
                  color: BlueberryColorsMap[FeatureEnum.blueberry_Purple],
                }}
              >
                {t(FeatureEnum.blueberry_Purple, { ns: 'common' })}
              </TableCell>
              <TableCell
                align='center'
                sx={{
                  ...cellStyles,
                  color: BlueberryColorsMap[FeatureEnum.blueberry_Blue],
                }}
              >
                {t(FeatureEnum.blueberry_Blue, { ns: 'common' })}
              </TableCell>
              <TableCell align='center' sx={{ ...cellStyles }}>
                {t('Total Berries', { ns: 'common' })}
              </TableCell>
              <TableCell align='center' sx={{ ...cellStyles }}>
                {t(
                  `Traveled Distance (${
                    units === UnitsEnum.imperial ? 'ft' : 'm'
                  })`,
                  { ns: 'ranch-view' }
                )}
              </TableCell>
              <TableCell align='center' sx={{ ...cellStyles }}>
                {t(
                  `Velocity (${units === UnitsEnum.imperial ? 'MPH' : 'KPH'})`,
                  { ns: 'ranch-view' }
                )}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.rowName}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell
                  component='th'
                  scope='row'
                  sx={{ ...firstColumnStyles }}
                >
                  {row.rowName}
                </TableCell>
                <TableCell
                  align='center'
                  sx={{
                    ...cellStyles,
                    color: BlueberryColorsMap[FeatureEnum.flower_closed],
                  }}
                >
                  {row.flowersClosed}
                </TableCell>
                <TableCell
                  align='center'
                  sx={{
                    ...cellStyles,
                    color: BlueberryColorsMap[FeatureEnum.flower_open],
                  }}
                >
                  {row.flowersOpen}
                </TableCell>
                <TableCell
                  align='center'
                  sx={{
                    ...cellStyles,
                    color: BlueberryColorsMap[FeatureEnum.flower_petalfall],
                  }}
                >
                  {row.flowersPetalfall}
                </TableCell>
                <TableCell align='center' sx={{ ...cellStyles }}>
                  {row.flowersTotal}
                </TableCell>
                <TableCell
                  align='center'
                  sx={{
                    ...cellStyles,
                    color: BlueberryColorsMap[FeatureEnum.blueberry_Green],
                  }}
                >
                  {row.green}
                </TableCell>
                <TableCell
                  align='center'
                  sx={{
                    ...cellStyles,
                    color: BlueberryColorsMap[FeatureEnum.blueberry_Yellow],
                  }}
                >
                  {row.pink}
                </TableCell>
                <TableCell
                  align='center'
                  sx={{
                    ...cellStyles,
                    color: BlueberryColorsMap[FeatureEnum.blueberry_Purple],
                  }}
                >
                  {row.purple}
                </TableCell>
                <TableCell
                  align='center'
                  sx={{
                    ...cellStyles,
                    color: BlueberryColorsMap[FeatureEnum.blueberry_Blue],
                  }}
                >
                  {row.blue}
                </TableCell>
                <TableCell align='center' sx={{ ...cellStyles }}>
                  {row.berriesTotal}
                </TableCell>
                <TableCell align='center' sx={{ ...cellStyles }}>
                  {row.totalDistance}
                </TableCell>
                <TableCell align='center' sx={{ ...cellStyles }}>
                  {row.avgVelocity}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </TableContainer>
  );
};

export default MapMeasureTableBlueberries;
